import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LottieFavoriteAdd } from 'src/shared/design-system/Animations';
import { match } from 'ts-pattern';
import { Icon, IconColor, IconSize } from 'web-common/src/shared/design-system/components/Icon';

const WIGGLE_CLASS = 'animate-heart-wiggle';
// Fallback so the button can't get wedged into animation mode if Lottie's `complete` event misses.
const STUCK_RESET_MS = 1500;

type AnimationType = 'add' | 'none';

interface Props {
  isActive: boolean;
  /** Returns whether the toggle was successful — gates the celebrate animation. */
  onToggle: (e: React.MouseEvent<HTMLElement, MouseEvent>) => Promise<boolean>;
  /** Tailwind class applied to the outer wrapper — control hit-target size here (e.g. `size-12`, `size-14`). */
  className?: string;
  iconSize?: IconSize;
}

/** Mid-animation taps wiggle the wrapper instead of restarting the Lottie underneath. */
/**
 * @deprecated The shef-web design system is being retired. web-common has no
 *   counterpart yet — see the migration ticket before adding a new call site.
 * @see https://app.asana.com/0/0/1218340372010821
 */
export const HeartToggleButton: React.FC<Props> = ({ isActive, onToggle, className, iconSize = 32 }) => {
  const [animationType, setAnimationType] = useState<AnimationType>('none');
  const wrapperRef = useRef<HTMLButtonElement>(null);

  const handleAnimationComplete = useCallback(() => {
    setAnimationType('none');
  }, []);

  useEffect(() => {
    if (animationType === 'none') return undefined;
    const t = setTimeout(() => setAnimationType('none'), STUCK_RESET_MS);
    return () => clearTimeout(t);
  }, [animationType]);

  // Imperative restart: remove class, force reflow, re-add. Avoids `key`-remounting the Lottie underneath.
  const triggerWiggle = () => {
    const el = wrapperRef.current;
    if (!el) return;
    el.classList.remove(WIGGLE_CLASS);
    // eslint-disable-next-line no-void -- force a layout flush so class removal lands before re-add
    void el.offsetHeight;
    el.classList.add(WIGGLE_CLASS);
  };

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const isMidAnimation = animationType !== 'none';
      if (isMidAnimation) {
        triggerWiggle();
        return;
      }
      // Celebrate only on inactive → active. Set animation eagerly so the user feels the response,
      // and revert on mutation failure.
      if (!isActive) setAnimationType('add');
      try {
        const ok = await onToggle(e);
        if (!ok && !isActive) setAnimationType('none');
      } catch {
        if (!isActive) setAnimationType('none');
      }
    },
    [animationType, isActive, onToggle]
  );

  const content = match(animationType)
    .with('add', () => <LottieFavoriteAdd onCheckAnimationEnd={handleAnimationComplete} className='size-full' />)
    .otherwise(() => (
      <Icon name={isActive ? 'heartFilled' : 'heartUnfilled'} color={IconColor.RASPBERRY} size={iconSize} />
    ));

  return (
    <button
      ref={wrapperRef}
      type='button'
      aria-pressed={isActive}
      aria-label={isActive ? 'Remove from favorites' : 'Add to favorites'}
      onClick={handleClick}
      className={cn('flex cursor-pointer items-center justify-center', className)}>
      {content}
    </button>
  );
};
